import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
  ArrowRight, 
  Target, 
  Lightbulb, 
  TrendingUp, 
  Users, 
  Award,
  Briefcase,
  Sparkles,
  CheckCircle2,
  BarChart3,
  Globe,
  Zap,
  Mail
} from 'lucide-react';
import {
  AchievementsSection,
  CaseStudiesSection,
  CapabilitiesSection,
  WhyWorkSection,
  EngagementSection
} from './PortfolioSections';

const Portfolio = () => {
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      let current = '';
      
      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.clientHeight;
        if (window.pageYOffset >= sectionTop - 200) {
          current = section.getAttribute('id');
        }
      });
      
      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="portfolio-container">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-xl text-slate-900">
              Lopamudra Misra
            </div>
            <div className="hidden md:flex items-center gap-8">
              <button 
                onClick={() => scrollToSection('services')} 
                className={`text-sm font-medium transition-colors hover:text-blue-600 ${
                  activeSection === 'services' ? 'text-blue-600' : 'text-slate-600'
                }`}
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection('case-studies')} 
                className={`text-sm font-medium transition-colors hover:text-blue-600 ${
                  activeSection === 'case-studies' ? 'text-blue-600' : 'text-slate-600'
                }`}
              >
                Case Studies
              </button>
              <button 
                onClick={() => scrollToSection('engagement')} 
                className={`text-sm font-medium transition-colors hover:text-blue-600 ${
                  activeSection === 'engagement' ? 'text-blue-600' : 'text-slate-600'
                }`}
              >
                Engagement
              </button>
              <Button 
                onClick={() => scrollToSection('contact')}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Get in Touch
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 -z-10"></div>
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <div className="flex flex-wrap gap-3 mb-6">
              <Badge variant="secondary" className="text-slate-700 bg-slate-100">
                Fractional Product Leader
              </Badge>
              <Badge variant="secondary" className="text-slate-700 bg-slate-100">
                AI Strategy
              </Badge>
              <Badge variant="secondary" className="text-slate-700 bg-slate-100">
                SaaS Growth
              </Badge>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 mb-6 leading-tight">
              Product Strategy that{' '}
              <span className="italic text-blue-600">moves</span> the needle.
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed max-w-3xl">
              Senior product executive with 10+ years building AI-powered enterprise products at Cisco and VMware. 
              Available for fractional engagements, advisory, and product consulting.
            </p>
            <Button 
              onClick={() => scrollToSection('engagement')}
              size="lg" 
              className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-6 group"
            >
              Explore Working Together
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">10+</div>
              <div className="text-sm text-slate-600 font-medium">Years Product Leadership</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">$24B+</div>
              <div className="text-sm text-slate-600 font-medium">Revenue Portfolio Managed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">30%</div>
              <div className="text-sm text-slate-600 font-medium">Reduction in Manual Workflow Time</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">2</div>
              <div className="text-sm text-slate-600 font-medium">Tier-1 Enterprise SaaS Organisations</div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section id="introduction" className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <div className="text-sm font-semibold text-blue-600 mb-4 tracking-wide uppercase">01 — Executive Introduction</div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">Profile & Expertise</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <div>
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                Lopamudra Misra is a Product Leader with 10+ years of experience building and scaling AI-powered enterprise SaaS products 
                across customer experience, renewal intelligence, and network infrastructure. She has led product strategy at Cisco and VMware — 
                two of the world's most complex enterprise technology organisations — owning roadmaps that support multi-billion dollar business outcomes.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                Her work sits at the intersection of <strong>GenAI product development, behavioural analytics, and customer success strategy</strong>. 
                She has shipped capabilities that directly reduced customer churn, accelerated renewal cycles, and created defensible competitive 
                advantages through intelligent automation.
              </p>
            </div>
            <div>
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                Today, she makes her expertise available to founders and growth-stage companies as a <strong>Fractional Product Manager, 
                AI Product Consultant, and Product Strategy Advisor</strong> — helping them build with the same rigour and commercial clarity 
                that defines product management at the world's best enterprise technology companies.
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg">
                <p className="text-slate-700 italic leading-relaxed">
                  "I don't just build features. I build the strategic case for what to build, why now, and how to measure whether it worked."
                </p>
                <p className="text-slate-600 text-sm mt-3 font-medium">— Lopamudra Misra</p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="text-2xl text-slate-900">Core Expertise</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {[
                    'AI Product Management & GenAI Workflows',
                    'Product Strategy & Roadmap Architecture',
                    'Customer Experience & Renewal Optimization',
                    'Enterprise SaaS & Subscription Platforms',
                    'Behavioral Analytics & Product Insights',
                    'Cross-functional Leadership & Stakeholder Management',
                    'Agile Delivery & Scrum Practices',
                    'Go-to-Market Strategy & Adoption'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="text-2xl text-slate-900">Industries Served</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {[
                    'Enterprise SaaS',
                    'AI / GenAI',
                    'Networking',
                    'Customer Success',
                    'Cloud Infrastructure',
                    'Analytics',
                    'B2B Tech',
                    'Startups'
                  ].map((industry, idx) => (
                    <Badge key={idx} variant="outline" className="text-slate-700 border-slate-300 px-4 py-2">
                      {industry}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Product Philosophy */}
          <div className="mt-16 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-12 text-white">
            <div className="max-w-3xl">
              <div className="text-sm font-semibold text-blue-400 mb-4 tracking-wide uppercase">Product Philosophy</div>
              <h3 className="text-3xl md:text-4xl font-bold mb-6">Products should earn their place in the workflow.</h3>
              <p className="text-lg text-slate-300 leading-relaxed">
                I believe the best product decisions are made at the intersection of <strong className="text-white">customer empathy, business strategy, 
                and technical reality</strong>. Features don't create value — outcomes do. Whether I'm defining a 0→1 AI capability or rationalising 
                a bloated roadmap, my north star is always the same: <strong className="text-white">does this move a metric that matters to the business 
                and delight the user who uses it?</strong> I bring a consulting mindset to product leadership — structured thinking, executive communication, 
                and a bias towards learning fast and delivering incrementally.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <div className="text-sm font-semibold text-blue-600 mb-4 tracking-wide uppercase">02 — Services Offered</div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Consulting Engagements</h2>
            <p className="text-xl text-slate-600 max-w-3xl">Strategic product expertise tailored to your growth stage and business needs.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <ServiceCard
              number="01"
              category="Embedded Leadership"
              title="Fractional Product Management"
              description="An experienced VP-level product mind embedded in your team at a fraction of the cost. Ideal for seed-to-Series B companies that need senior product leadership without a full-time hire."
              features={[
                'Own the product roadmap and backlog',
                'Lead sprint planning and stakeholder reviews',
                'Manage engineering and design partnerships',
                'Define metrics, OKRs, and success criteria',
                'Drive product culture and process maturity'
              ]}
              icon={<Briefcase className="h-6 w-6" />}
            />
            <ServiceCard
              number="02"
              category="Strategic Advisory"
              title="Product Strategy & Roadmap Consulting"
              description="Clarity on what to build, when, and why. For founders or CPOs who need a structured outside perspective to prioritise ruthlessly and build a roadmap that investors, engineers, and customers all believe in."
              features={[
                'Market positioning and competitive analysis',
                'Opportunity sizing and prioritisation frameworks',
                '12–18 month roadmap development',
                'Strategic narrative for board and investors',
                'Outcome-based success metrics definition'
              ]}
              icon={<Target className="h-6 w-6" />}
            />
            <ServiceCard
              number="03"
              category="AI Expertise"
              title="AI Product Strategy"
              description="Hands-on expertise designing AI-native product experiences. From identifying the right AI use case to evaluating build-vs-buy, prompt engineering, and measuring GenAI feature adoption in enterprise contexts."
              features={[
                'AI use case identification and feasibility assessment',
                'GenAI workflow design and prompt strategy',
                'AI product experimentation frameworks',
                'Responsible AI product considerations',
                'AI-driven KPI definition and adoption playbooks'
              ]}
              icon={<Sparkles className="h-6 w-6" />}
            />
            <ServiceCard
              number="04"
              category="Discovery & Research"
              title="Product Discovery & Customer Research"
              description="Move from assumptions to validated insights. Structured discovery sprints that combine user interviews, data analysis, and Jobs-to-be-Done frameworks to surface the problems worth solving."
              features={[
                'Customer interview design and facilitation',
                'Persona and journey mapping',
                'Jobs-to-be-Done analysis',
                'Insight synthesis and prioritisation',
                'Opportunity solution trees'
              ]}
              icon={<Users className="h-6 w-6" />}
            />
            <ServiceCard
              number="05"
              category="Launch Strategy"
              title="Go-to-Market Strategy"
              description="Bridge the gap between product and commercial success. For product teams launching new features or entering new markets who need a rigorous GTM plan aligned with sales, marketing, and customer success."
              features={[
                'Launch readiness assessment',
                'Messaging and positioning strategy',
                'Channel and segment targeting',
                'Feature adoption and onboarding design',
                'Sales enablement and training materials'
              ]}
              icon={<TrendingUp className="h-6 w-6" />}
            />
            <ServiceCard
              number="06"
              category="Diagnostic Review"
              title="Product Audit & Growth Recommendations"
              description="An objective, structured review of your product's health — strategy, UX, adoption, monetisation, and competitive position — delivered as an executive-grade report with a clear action plan."
              features={[
                'Product-market fit assessment',
                'Activation, retention, and churn analysis',
                'Roadmap coherence and prioritisation review',
                'Feature usage and value gap analysis',
                'Executive summary with 90-day action plan'
              ]}
              icon={<BarChart3 className="h-6 w-6" />}
            />
          </div>
        </div>
      </section>

      {/* Other Sections */}
      <AchievementsSection />
      <CaseStudiesSection />
      <CapabilitiesSection />
      <WhyWorkSection />
      <EngagementSection />

      {/* Footer */}
      <footer id="contact" className="py-16 px-6 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <h3 className="text-2xl font-bold mb-4">Lopamudra Misra</h3>
              <p className="text-slate-400 leading-relaxed">
                Product Strategy · AI Advisory · Fractional Product Leadership
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-slate-400">
                <li><button onClick={() => scrollToSection('services')} className="hover:text-white transition-colors">Services</button></li>
                <li><button onClick={() => scrollToSection('case-studies')} className="hover:text-white transition-colors">Case Studies</button></li>
                <li><button onClick={() => scrollToSection('engagement')} className="hover:text-white transition-colors">Engagement Models</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Get in Touch</h4>
              <a 
                href="mailto:lopa.misra@gmail.com"
                data-testid="footer-email-link"
                className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              >
                <Mail className="h-5 w-5" />
                <span>lopa.misra@gmail.com</span>
              </a>
              <p className="text-slate-500 text-sm mt-2">Available for consulting inquiries</p>
            </div>
          </div>
          <Separator className="bg-slate-800 mb-8" />
          <div className="text-center text-slate-400 text-sm">
            © 2025 Lopamudra Misra. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

const ServiceCard = ({ number, category, title, description, features, icon }) => (
  <Card className="border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
    <CardHeader>
      <div className="flex items-center gap-3 mb-3">
        <div className="h-12 w-12 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
          {icon}
        </div>
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{number} — {category}</div>
        </div>
      </div>
      <CardTitle className="text-2xl text-slate-900 mb-3">{title}</CardTitle>
      <CardDescription className="text-slate-600 leading-relaxed text-base">{description}</CardDescription>
    </CardHeader>
    <CardContent>
      <ul className="space-y-2">
        {features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
            <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </CardContent>
  </Card>
);

export default Portfolio;
